import { Dispatch, SetStateAction, useState } from "react"

// ** Third Party Imports
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { isPossiblePhoneNumber } from "libphonenumber-js"

// ** Custom Hooks Imports
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Types
import { DictType } from "@pma/types"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { EDIT_USER_ORG_PARAMETER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

interface PhoneEditorProps {
  phoneNumber: string
  userId: string
  refetch: CallableFunction
  setEditMode: Dispatch<SetStateAction<boolean>>
}

const PhoneEditor = ({ userId, refetch, phoneNumber, setEditMode }: PhoneEditorProps) => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [phoneInputValue, setPhoneInputValue] = useState(phoneNumber || "")

  const updateUserDetails = async () => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,
      {
        user_id: userId,
        phone: phoneInputValue,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const updateUserPhoneNumberMutation = useMutation({
    mutationKey: [`roles`],
    mutationFn: () => updateUserDetails(),
    onSuccess: (data) => {
      refetch && refetch()
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
          translate(`Error occurred while updating`) + " " + translate(`role`),
        variant: "error",
      })
    },
  })

  const onSubmit = () => {
    updateUserPhoneNumberMutation.mutate()
    setEditMode(false)
  }

  return (
    <div className='flex flex-col'>
      <input
        className='w-[15ch] rounded border bg-white/20 px-2 outline-none focus:border-primary'
        value={phoneInputValue}
        onChange={(e) => setPhoneInputValue(e?.target?.value)}
        autoFocus
        onBlur={() => onSubmit()}
        onKeyDown={(e) => {
          if (e.key == "Enter") onSubmit()
        }}
      />
      {!isPossiblePhoneNumber(String(phoneInputValue)) && (
        <small className='px-3 text-xs text-destructive'>
          {translate("Enter a valid number") + "i.e +88"}
        </small>
      )}
    </div>
  )
}

export default PhoneEditor
